/**
 * WalletScroller — poziomy snap scroll z kartami portfeli.
 * Każda karta: kolor pasek, nazwa, balance (tabular-nums), sparkline 30-dni.
 * Selected wallet: ring sky-500 + scale-[1.02].
 */
import { useMemo } from 'react';
import { useWalletsStore } from '../../features/wallets/walletsStore';
import { useTransactionsStore } from '../../features/transactions/transactionsStore';
import {
  computeWalletBalance,
  computeWalletSparkline,
} from '../../features/transactions/selectors';
import { formatCurrency, sparklinePoints } from '../../lib/utils';
import { Icons } from '../Icons';

export function WalletScroller() {
  const wallets = useWalletsStore((s) => s.wallets);
  const selectedWalletId = useWalletsStore((s) => s.selectedWalletId);
  const selectWallet = useWalletsStore((s) => s.selectWallet);
  const transactions = useTransactionsStore((s) => s.transactions);

  // Sortuj: shared (Wspólny) pierwszy, potem osobiste
  const sorted = useMemo(
    () =>
      [...wallets].sort((a, b) => {
        if (a.type === 'shared' && b.type !== 'shared') return -1;
        if (b.type === 'shared' && a.type !== 'shared') return 1;
        return a.name.localeCompare(b.name);
      }),
    [wallets],
  );

  // Pseudo-wallet "Wspólny" gdy żaden faktyczny portfel typu 'shared' nie istnieje
  // (legacy users mają tylko personal). id='all' → DashboardPage filtruje
  // po walletFilter=null (agregat całej rodziny).
  const hasShared = wallets.some((w) => w.type === 'shared');
  const showPseudoShared = !hasShared && wallets.length > 0;

  if (wallets.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
        <Icons.Wallet size={32} className="mx-auto mb-2 opacity-40" />
        Brak portfeli — utwórz pierwszy w Ustawieniach
      </div>
    );
  }

  return (
    <div className="-mx-4 px-4 pb-2 overflow-x-auto snap-x snap-mandatory flex gap-3 scrollbar-thin">
      {showPseudoShared && (() => {
        const isSelected = selectedWalletId === 'all';
        const balance = computeWalletBalance(transactions, 'all', true);
        const points = computeWalletSparkline(transactions, 'all', 30, true);
        const sparkline = sparklinePoints(points, { w: 120, h: 28 });
        return (
          <button
            key="pseudo-all"
            type="button"
            onClick={() => selectWallet('all')}
            className={`shrink-0 snap-start w-[180px] sm:w-[200px] rounded-card bg-white dark:bg-slate-800 border ${
              isSelected
                ? 'border-sky-500 dark:border-sky-400 ring-2 ring-sky-500/20 scale-[1.02]'
                : 'border-slate-200 dark:border-slate-700'
            } shadow-card text-left p-4 transition-all`}
            aria-pressed={isSelected}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-gradient-to-br from-sky-500 to-emerald-500" aria-hidden="true" />
              <span className="text-[9px] uppercase tracking-wide text-slate-400 dark:text-slate-500 font-bold">
                Wspólny
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-0.5 truncate">Cała rodzina</p>
            <p className={`text-lg font-bold tabular-nums leading-none ${balance < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-white'}`}>
              {formatCurrency(balance)}
            </p>
            {sparkline && (
              <svg viewBox="0 0 120 28" className="mt-2 w-full h-7" preserveAspectRatio="none" aria-hidden="true">
                <polyline
                  fill="none"
                  stroke={isSelected ? 'rgb(2 132 199)' : 'rgb(148 163 184)'}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  points={sparkline}
                />
              </svg>
            )}
          </button>
        );
      })()}
      {sorted.map((w) => {
        const isSelected = w.id === selectedWalletId;
        const aggregateAll = w.type === 'shared';
        const balance = computeWalletBalance(transactions, w.id, aggregateAll);
        const points = computeWalletSparkline(transactions, w.id, 30, aggregateAll);
        const sparkline = sparklinePoints(points, { w: 120, h: 28 });
        return (
          <button
            key={w.id}
            type="button"
            onClick={() => selectWallet(w.id)}
            className={`shrink-0 snap-start w-[180px] sm:w-[200px] rounded-card bg-white dark:bg-slate-800 border ${
              isSelected
                ? 'border-sky-500 dark:border-sky-400 ring-2 ring-sky-500/20 scale-[1.02]'
                : 'border-slate-200 dark:border-slate-700'
            } shadow-card text-left p-4 transition-all`}
            aria-pressed={isSelected}
          >
            <div className="flex items-center justify-between mb-2">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: w.color }}
                aria-hidden="true"
              />
              {w.type === 'shared' && (
                <span className="text-[9px] uppercase tracking-wide text-slate-400 dark:text-slate-500 font-bold">
                  Wspólny
                </span>
              )}
            </div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-0.5 truncate">{w.name}</p>
            <p
              className={`text-lg font-bold tabular-nums leading-none ${
                balance < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-white'
              }`}
            >
              {formatCurrency(balance)}
            </p>
            {sparkline && (
              <svg
                viewBox="0 0 120 28"
                className="mt-2 w-full h-7"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <polyline
                  fill="none"
                  stroke={isSelected ? 'rgb(2 132 199)' : 'rgb(148 163 184)'}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  points={sparkline}
                />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}
